(() => {
  if (customElements.get('witness-ritual')) return;
  class WitnessRitual extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const signal = this.abort.signal;
      const listen = (node, type, fn, options = {}) => node.addEventListener(type, fn, { ...options, signal });
      const track = this.querySelector('[data-ritual-track]');
      if (!track) return;
      const prev = this.querySelector('[data-ritual-prev]');
      const next = this.querySelector('[data-ritual-next]');
      const arrows = this.querySelector('[data-ritual-arrows]');
      const videos = [...this.querySelectorAll('video')];
      const sync = () => {
        const max = track.scrollWidth - track.clientWidth;
        arrows.hidden = max < 2;
        prev.disabled = track.scrollLeft <= 1;
        next.disabled = track.scrollLeft >= max - 1;
      };
      const step = direction => {
        const card = track.querySelector('[data-ritual-card]');
        if (!card) return;
        track.scrollBy({ left: direction * (card.getBoundingClientRect().width + parseFloat(getComputedStyle(track).gap || 0)), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      };
      listen(prev, 'click', () => step(-1));
      listen(next, 'click', () => step(1));
      listen(track, 'scroll', sync, { passive: true });
      listen(window, 'resize', sync);
      this.resize = new ResizeObserver(sync);
      this.resize.observe(track);
      videos.forEach(video => {
        const card = video.closest('[data-ritual-card]');
        const play = card.querySelector('[data-ritual-play]');
        const error = card.querySelector('[data-ritual-error]');
        video.controls = false;
        play.hidden = false;
        const restore = () => { play.hidden = false; video.controls = false; };
        listen(play, 'click', async () => {
          error.hidden = true;
          videos.forEach(other => { if (other !== video) other.pause(); });
          play.hidden = true;
          video.controls = true;
          try { await video.play(); } catch { restore(); error.hidden = false; }
        });
        listen(video, 'play', () => {
          videos.forEach(other => { if (other !== video) other.pause(); });
          play.hidden = true; video.controls = true;
        });
        listen(video, 'ended', restore);
        listen(video, 'error', () => { restore(); error.hidden = false; });
      });
      this.visibility = new IntersectionObserver(entries => {
        entries.forEach(entry => { if (!entry.isIntersecting) entry.target.pause(); });
      }, { root: track, threshold: 0.05 });
      videos.forEach(video => this.visibility.observe(video));
      listen(document, 'visibilitychange', () => { if (document.hidden) videos.forEach(video => video.pause()); });
      listen(document, 'shopify:block:select', event => {
        const card = event.target.closest('[data-ritual-card]');
        if (card && this.contains(card)) track.scrollTo({left: card.offsetLeft - track.firstElementChild.offsetLeft, behavior: 'instant'});
      });
      let drag;
      let suppressClick = false;
      listen(track, 'pointerdown', event => {
        if (event.pointerType !== 'mouse' || event.button !== 0 || event.target.closest('video')) return;
        suppressClick = false;
        drag = { id: event.pointerId, x: event.clientX, left: track.scrollLeft, moved: false };
      });
      listen(track, 'pointermove', event => {
        if (!drag || drag.id !== event.pointerId) return;
        const delta = event.clientX - drag.x;
        if (!drag.moved && Math.abs(delta) < 5) return;
        drag.moved = true; suppressClick = true;
        track.classList.add('is-dragging');
        track.setPointerCapture(event.pointerId);
        event.preventDefault();
        track.scrollLeft = drag.left - delta;
      });
      const end = event => {
        if (!drag || event.pointerId !== drag.id) return;
        if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
        drag = null; track.classList.remove('is-dragging'); sync();
      };
      listen(track, 'pointerup', end);
      listen(track, 'pointercancel', end);
      listen(track, 'pointerleave', event => { if (drag && !drag.moved) end(event); });
      listen(track, 'click', event => {
        if (suppressClick) { event.preventDefault(); event.stopImmediatePropagation(); suppressClick = false; }
      }, { capture: true });
      listen(track, 'dragstart', event => event.preventDefault());
      sync();
    }
    disconnectedCallback() {
      this.querySelectorAll('video').forEach(video => video.pause());
      this.abort?.abort(); this.resize?.disconnect(); this.visibility?.disconnect();
    }
  }
  customElements.define('witness-ritual', WitnessRitual);
})();